import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import {
  DaySheet,
  DaySheetScope,
  DaySheetTask,
  normaliseDaySheet,
  siteDailyApis,
} from "../../../../api/SiteDaily";

// Shared plumbing for the Material-Stores and Mechanical day sheets: the tree
// fetch, the summary the page's StatCards/toolbar read, and the string-state
// number helpers every editor uses.

export type DaySheetKind = "materials" | "equipment";

export interface DaySheetFilters {
  date: string; // 'YYYY-MM-DD'
  scope: DaySheetScope;
  phaseId?: number;
  activityId?: number;
  // Only tasks the Civil section produced on the date.
  producedOnly?: boolean;
  // Material sheet: also tasks still owed material from earlier civil work.
  includePending?: boolean;
  // Every civil-completed task up to the date — Material / Mechanical are
  // booked against civil-done tasks, not a day.
  civilDone?: boolean;
}

// What a section tells the page about the sheet it holds — the page renders
// StatCards / the toolbar's scope toggle / the header Save-all from this.
export interface SheetSectionMeta {
  loading: boolean;
  // False until the first sheet lands — StatCards spin only then, not on
  // every filter-driven refresh.
  hasSheet: boolean;
  taskCount: number;
  recordedTaskCount: number;
  producedCount: number;
  monitoringConfigured: boolean;
  dayLocked: boolean;
  scope: DaySheetScope;
}

export const EMPTY_SHEET_META: SheetSectionMeta = {
  loading: false,
  hasSheet: false,
  taskCount: 0,
  recordedTaskCount: 0,
  producedCount: 0,
  monitoringConfigured: false,
  dayLocked: false,
  scope: "ALL",
};

export interface SheetSectionHandle {
  saveAll: () => Promise<void>;
  // Drop every draft and collapse every task — what "Discard" must actually
  // do when the section stays mounted (scope / phase / activity change).
  discardAll: () => void;
}

export const sheetMeta = (sheet: DaySheet | null, loading: boolean): SheetSectionMeta => {
  if (!sheet) return { ...EMPTY_SHEET_META, loading };
  let producedCount = 0;
  sheet.phases.forEach((phase) =>
    phase.activities.forEach((activity) =>
      activity.tasks.forEach((task) => {
        if (task.producedQty !== "") producedCount += 1;
      })
    )
  );
  return {
    loading,
    hasSheet: true,
    taskCount: sheet.taskCount,
    recordedTaskCount: sheet.recordedTaskCount,
    producedCount,
    monitoringConfigured: sheet.monitoringConfigured,
    dayLocked: sheet.dayLocked,
    scope: sheet.scope,
  };
};

/**
 * The phase → activity → task tree for one day. The previous sheet is kept on
 * screen while a filter change reloads (no skeleton flash); `reloadToken` is a
 * counter the toolbar's refresh button bumps.
 */
export const useDaySheet = (
  kind: DaySheetKind,
  projectId: number,
  filters: DaySheetFilters,
  reloadToken: number
) => {
  const [sheet, setSheet] = useState<DaySheet | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Only the newest request may land — a slow earlier day must not overwrite
  // the day the user has since moved to.
  const requestRef = useRef(0);
  const { date, scope, phaseId, activityId, producedOnly, includePending, civilDone } = filters;

  const load = useCallback(() => {
    if (!projectId || !date) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    const call = kind === "materials" ? siteDailyApis.materialTasks : siteDailyApis.equipmentTasks;
    call(projectId, { date, scope, phaseId, activityId, producedOnly, includePending, civilDone })
      .then((res) => {
        if (requestId !== requestRef.current) return;
        if (res?.status !== true) {
          // Never leave a previous day's rows/counts/lock fused to the new
          // filters — no sheet is the honest state.
          setSheet(null);
          setError(res?.message || "Could not load the day sheet");
          return;
        }
        setError("");
        setSheet(normaliseDaySheet(res.data));
      })
      .catch(() => {
        if (requestId !== requestRef.current) return;
        setSheet(null);
        setError("Could not load the day sheet");
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  }, [kind, projectId, date, scope, phaseId, activityId, producedOnly, includePending, civilDone]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  return { sheet, loading, error, reload: load };
};

// ------------------------------------------------ string-state number helpers
// Every numeric input keeps a STRING: '' is "not entered" and '0' is a real
// zero, and the two must never collapse into each other. antd's InputNumber in
// stringMode round-trips strings without float drift.

export const inputValue = (value: string): string | null => (value === "" ? null : value);

export const fromInput = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === "" ? "" : String(value);

export const isPresent = (value: string): boolean =>
  value !== "" && Number.isFinite(Number(value));

export const gt = (a: string, b: string): boolean =>
  isPresent(a) && isPresent(b) && Number(a) > Number(b);

export const lt = (a: string, b: string): boolean =>
  isPresent(a) && isPresent(b) && Number(a) < Number(b);

/** qty × rate at 2 dp as a string, or '' when either side is blank. */
export const amountOf = (qty: string, rate: string): string =>
  isPresent(qty) && isPresent(rate)
    ? String(Math.round((Number(qty) * Number(rate) + Number.EPSILON) * 100) / 100)
    : "";

/**
 * "Save all changed" — one task after another, never in parallel, so a slow
 * server sees the writes in order. Failures don't stop the run; they are
 * summarised at the end and those tasks stay dirty.
 */
export const saveSequentially = async (
  labelledIds: Array<{ id: number; label: string }>,
  saveOne: (id: number) => Promise<boolean>
): Promise<void> => {
  const failed: string[] = [];
  for (const { id, label } of labelledIds) {
    // eslint-disable-next-line no-await-in-loop
    const ok = await saveOne(id);
    if (!ok) failed.push(label);
  }
  const saved = labelledIds.length - failed.length;
  if (failed.length === 0) {
    message.success(`Saved ${saved} task${saved === 1 ? "" : "s"}`);
  } else {
    message.warning(
      `Saved ${saved} of ${labelledIds.length} — could not save ${failed.join(", ")}`,
      6
    );
  }
};

// ---- ₹ per unit of the task's output for the day -------------------------
// amount ÷ what the Civil section produced on the task that day, in the task's
// work unit. Actual cost over actual output — never over the planned
// quantity, which would turn a volume shortfall into a fake rate variance.
export const perUnitOf = (amount: number | string | null | undefined, producedQty: string): number | null => {
  if (amount === null || amount === undefined || amount === "") return null;
  const a = Number(amount);
  const q = Number(producedQty);
  if (producedQty === "" || !Number.isFinite(a) || !Number.isFinite(q) || q <= 0) return null;
  return a / q;
};

// "Cubic Meter" reads as Cum on a cost; any other unit is shown as it is.
// ("Cubit" also matches: the general uom_master has a misspelled id-3 row that
// a wrong-table join once surfaced here — harmless to keep tolerating.)
export const perUnitLabel = (workUomName: string): string =>
  /cubi[ct]\s*met/i.test(workUomName) ? "Cum" : workUomName || "unit";

// Rupees: 2 dp with Indian grouping; 4 dp below ₹1 so a small non-zero figure
// never prints as 0.00 and reads as free. (fmtQty keeps 4 dp — a QUANTITY
// format — so it is deliberately not used here.)
export const fmtPerUnit = (value: number): string =>
  value !== 0 && Math.abs(value) < 1
    ? value.toFixed(4)
    : value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Narrow a civil-done sheet to what the clerk asked to see: by default only the
 * tasks still owed something (`pendingOf` > 0), optionally every civil-done
 * task, and in both cases a free-text match on task / activity / code. Groups
 * left empty are dropped. Counts are recomputed so the tree's badges agree.
 */
export const filterCivilSheet = (
  sheet: DaySheet | null,
  options: { pendingOnly: boolean; search: string; pendingOf: (task: DaySheetTask) => number }
): DaySheet | null => {
  if (!sheet) return sheet;
  const needle = options.search.trim().toLowerCase();
  const keep = (task: DaySheetTask, activityName: string) =>
    (!options.pendingOnly || options.pendingOf(task) > 0) &&
    (!needle ||
      task.taskName.toLowerCase().includes(needle) ||
      task.taskSequenceId.toLowerCase().includes(needle) ||
      activityName.toLowerCase().includes(needle));
  let taskCount = 0;
  let recordedTaskCount = 0;
  const phases = sheet.phases
    .map((phase) => {
      const activities = phase.activities
        .map((activity) => {
          const tasks = activity.tasks.filter((task) => keep(task, activity.activityName));
          const recorded = tasks.filter((task) => task.hasEntryForDate).length;
          return { ...activity, tasks, taskCount: tasks.length, recordedTaskCount: recorded };
        })
        .filter((activity) => activity.tasks.length > 0);
      const phaseTasks = activities.reduce((sum, a) => sum + a.taskCount, 0);
      const phaseRecorded = activities.reduce((sum, a) => sum + a.recordedTaskCount, 0);
      taskCount += phaseTasks;
      recordedTaskCount += phaseRecorded;
      return { ...phase, activities, taskCount: phaseTasks, recordedTaskCount: phaseRecorded };
    })
    .filter((phase) => phase.activities.length > 0);
  return { ...sheet, phases, taskCount, recordedTaskCount };
};
